/**
 * Energy-Efficient Audio Waveform Visualizer
 *
 * Uses CSS transforms (GPU-accelerated) instead of Canvas (CPU-bound).
 * Runs at 24fps instead of 60fps - visually identical, 60% less CPU.
 */

import { useEffect, useRef, useState } from 'react';

interface AudioWaveformProps {
  mediaStream: MediaStream | null;
  isActive: boolean;
  barCount?: number;
  className?: string;
}

// Target frame rate (24fps is enough for audio visualization)
const TARGET_FPS = 24;
const FRAME_INTERVAL = 1000 / TARGET_FPS;

export default function AudioWaveformEfficient({
  mediaStream,
  isActive,
  barCount = 32,
  className = ''
}: AudioWaveformProps) {
  const barsRef = useRef<(HTMLDivElement | null)[]>([]);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize audio analyser
  useEffect(() => {
    if (!mediaStream || !isActive) {
      setIsInitialized(false);
      return;
    }

    const initializeAnalyser = async () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext({ sampleRate: 24000 });
        }

        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        const analyser = audioContextRef.current.createAnalyser();
        analyser.fftSize = 64; // Smaller FFT = less computation
        analyser.smoothingTimeConstant = 0.75;

        const source = audioContextRef.current.createMediaStreamSource(mediaStream);
        source.connect(analyser);

        analyserRef.current = analyser;
        sourceRef.current = source;
        setIsInitialized(true);
      } catch (error) {
        console.error('🎵 [WAVEFORM] Failed to initialize:', error);
      }
    };

    initializeAnalyser();

    return () => {
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      analyserRef.current = null;
    };
  }, [mediaStream, isActive]);

  // Animation loop - uses setInterval for true frame rate limiting
  useEffect(() => {
    if (!isInitialized || !analyserRef.current || !isActive) {
      return;
    }

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    // Use setInterval - truly runs at TARGET_FPS, not 60fps with skipping
    const intervalId = setInterval(() => {
      // Get frequency data
      analyser.getByteFrequencyData(dataArray);

      // Update bar heights using CSS transform (GPU-accelerated)
      for (let i = 0; i < barCount; i++) {
        const bar = barsRef.current[i];
        if (!bar) continue;

        const dataIndex = Math.floor((i / barCount) * bufferLength * 0.8);
        const value = dataArray[dataIndex] || 0;
        const scale = Math.max(value / 255, 0.08);

        bar.style.transform = `scaleY(${scale})`;
      }
    }, FRAME_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [isInitialized, isActive, barCount]);

  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  // Pre-compute bar colors (gradient from blue to purple)
  const getBarColor = (index: number) => {
    const hue = 220 + (index / barCount) * 40;
    return `hsl(${hue}, 75%, 60%)`;
  };

  return (
    <div className={`relative ${className}`}>
      {/* Waveform bars container */}
      <div className="w-full h-full flex items-center justify-center gap-[2px]">
        {Array.from({ length: barCount }).map((_, i) => (
          <div
            key={i}
            ref={(el) => (barsRef.current[i] = el)}
            className="h-full rounded-sm"
            style={{
              width: `${100 / barCount - 0.5}%`,
              backgroundColor: isActive ? getBarColor(i) : '#d1d5db',
              transform: 'scaleY(0.08)',
              transformOrigin: 'center',
              willChange: isActive ? 'transform' : 'auto',
            }}
          />
        ))}
      </div>

      {/* Active indicator */}
      {isActive && (
        <div className="absolute top-1 right-2 flex items-center gap-1.5">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-[10px] text-gray-500 font-medium">LIVE</span>
        </div>
      )}
    </div>
  );
}
